import type { OrderTemplateFilters } from "@/types/orderTemplates";

/**
 * The wire form of {@link OrderTemplateFilters} — what axios is handed for
 * `GET /v1/order-templates/`.
 *
 * Booleans become the STRINGS django-filter's `BooleanFilter` parses, because a
 * JS `false` surviving a query serializer is not something to assume about: it
 * is asserted on the emitted query string in
 * `src/api/__tests__/orderTemplates.filters.test.ts`.
 */
export type OrderTemplateFilterParams = {
  tags?: string[];
  tags_match?: "all" | "any";
  created_at__date?: string;
  in_route_template?: "true" | "false";
  time_window_start__gte?: string;
  time_window_start__lte?: string;
};

/**
 * Projects the picker's filter state onto query params, dropping everything
 * empty.
 *
 * ## Why this lives in `lib/` and not beside `getOrderTemplates`
 *
 * It is a PURE function that three different layers need — the API call, the
 * paging hook's query key, and `isPoolFilterActive`'s gate. Nineteen test files
 * auto-mock `@/api/orderTemplates` wholesale (`vi.mock("@/api/orderTemplates")`),
 * which replaces every export with `undefined`; a hook importing this from there
 * would crash in each of them. A pure helper that non-mocked code depends on
 * therefore belongs outside the module tests mock for its network calls.
 *
 * ## Why "drop everything empty" is a requirement, not tidiness
 *
 * The unfiltered path must stay provably identical to what shipped before
 * Sprint 200: an `undefined`, `""` or `[]` that survives into the params object
 * becomes `?tags=` on the wire, and a blank value reaching a server-side
 * filterset is the difference between "every row" and "no rows" depending on how
 * that filter is written. So emptiness is resolved HERE, once, and a test pins
 * that an all-empty filter object produces `{}`.
 *
 * `tags_match` is only emitted alongside two or more tags: with one tag AND and
 * OR are the same statement, and the server defaults to `all` anyway.
 */
export function buildOrderTemplateFilterParams(
  filters?: OrderTemplateFilters,
): OrderTemplateFilterParams {
  if (!filters) return {};
  const params: OrderTemplateFilterParams = {};

  const tags = (filters.tags ?? [])
    .map((tag) => tag.trim())
    .filter((tag) => tag !== "");
  if (tags.length > 0) {
    params.tags = tags;
    if (tags.length > 1 && filters.tags_match === "any") {
      params.tags_match = "any";
    }
  }

  if (filters.created_at__date) {
    params.created_at__date = filters.created_at__date;
  }
  if (filters.in_route_template !== undefined) {
    params.in_route_template = filters.in_route_template ? "true" : "false";
  }
  if (filters.time_window_start__gte) {
    params.time_window_start__gte = filters.time_window_start__gte;
  }
  if (filters.time_window_start__lte) {
    params.time_window_start__lte = filters.time_window_start__lte;
  }

  return params;
}
